import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from './ui/popover';
import { Bell, AlertTriangle, Package, Check, CheckCheck } from 'lucide-react';

const NotificationCenter = () => {
    const { api, user, hasPermission } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Only show for DIRECTEUR and DIRECTEUR_ADJOINT
    const canView = user?.role === 'DIRECTEUR' || user?.role === 'DIRECTEUR_ADJOINT';

    useEffect(() => {
        if (canView) {
            fetchUnreadCount();
            // Poll every 60 seconds
            const interval = setInterval(fetchUnreadCount, 60000);
            return () => clearInterval(interval);
        }
    }, [canView]);

    const fetchUnreadCount = async () => {
        try {
            const res = await api().get('/notifications/unread-count');
            setUnreadCount(res.data.count);
        } catch (error) {
            console.error('Error fetching notification count:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const res = await api().get('/notifications?limit=10');
            setNotifications(res.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        try {
            await api().post(`/notifications/${notificationId}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? {...n, is_read: true} : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            await api().post('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({...n, is_read: true})));
            setUnreadCount(0);
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const handleOpen = (isOpen) => {
        setOpen(isOpen);
        if (isOpen) {
            fetchNotifications();
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'RED':
                return <AlertTriangle className="w-4 h-4 text-red-500" />;
            case 'ORANGE':
                return <Package className="w-4 h-4 text-orange-500" />;
            default:
                return <Bell className="w-4 h-4 text-blue-500" />;
        }
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 3600000) { // < 1 hour
            const mins = Math.floor(diff / 60000);
            return `il y a ${mins} min`;
        } else if (diff < 86400000) { // < 24 hours
            const hours = Math.floor(diff / 3600000);
            return `il y a ${hours}h`;
        } else {
            return date.toLocaleDateString('fr-FR');
        }
    };

    if (!canView) return null;

    return (
        <Popover open={open} onOpenChange={handleOpen}>
            <PopoverTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative"
                    data-testid="notification-bell"
                >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                        <Badge 
                            className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs"
                            data-testid="notification-badge"
                        >
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b">
                    <h3 className="font-semibold">Notifications</h3>
                    {unreadCount > 0 && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={markAllAsRead}
                            className="text-xs"
                        >
                            <CheckCheck className="w-3 h-3 mr-1" />
                            Tout lire
                        </Button>
                    )}
                </div>
                
                <div className="max-h-80 overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            Chargement...
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                            Aucune notification
                        </div>
                    ) : (
                        notifications.map(notification => (
                            <div 
                                key={notification.id}
                                className={`p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer ${
                                    !notification.is_read ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => !notification.is_read && markAsRead(notification.id)}
                            >
                                <div className="flex items-start gap-3">
                                    {getSeverityIcon(notification.severity)}
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm ${!notification.is_read ? 'font-medium' : ''}`}>
                                            {notification.title}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">
                                            {notification.message}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {formatTime(notification.created_at)}
                                        </p>
                                    </div>
                                    {!notification.is_read && (
                                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <div className="p-2 border-t">
                    <Link to="/reorder" onClick={() => setOpen(false)}>
                        <Button variant="ghost" size="sm" className="w-full">
                            Voir le réapprovisionnement
                        </Button>
                    </Link>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default NotificationCenter;
